import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { UserPlus, X, Eye, Pencil, Users as UsersIcon, Wand2, Clock, AlertTriangle, ClipboardList, MapPin } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import IndividualEditor from "@/components/IndividualEditor";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 50;

const STATUS_STYLE = {
  intake:      "bg-amber-100 text-amber-800",
  active:      "bg-green-100 text-green-800",
  discharged:  "bg-slate-200 text-slate-700",
  deceased:    "bg-slate-400 text-white",
  transferred: "bg-blue-100 text-blue-800",
};

export default function Individuals() {
  const { user } = useAuth();
  const canManage = user && (user.role === "admin" || user.role === "rn");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [includeDischarged, setIncludeDischarged] = useState(false);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editor, setEditor] = useState(null);   // { individual, initialTab } | null
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", preferred_name: "",
    dob: "", intake_date: new Date().toISOString().slice(0, 10),
    primary_language: "English",
    county: "", mobility_notes: "",
    // ADR-0016 — participant demographic + contact
    phone: "", email: "",
    home_address: { street: "", unit: "", city: "", state: "", zip: "", county: "" },
    mailing_same: true,
    mailing_address: { street: "", unit: "", city: "", state: "", zip: "", county: "" },
  });

  const load = (p = page) =>
    api.get("/individuals", {
      params: {
        page: p, limit: PAGE_SIZE,
        search: search || undefined,
        include_discharged: includeDischarged || undefined,
      },
    }).then((r) => {
      setRows(r.data.data);
      setPage(r.data.page);
      setPages(r.data.pages);
      setTotal(r.data.total);
    });

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, [includeDischarged]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // ADR-0016 — collapse empty address objects to null on submit so we
      // don't persist an all-empty object masquerading as a filled address.
      const addrOrNull = (a) => (a && Object.values(a).some((v) => v && v.trim())) ? a : null;
      const payload = {
        ...form,
        home_address: addrOrNull(form.home_address),
        mailing_address: form.mailing_same
          ? addrOrNull(form.home_address)
          : addrOrNull(form.mailing_address),
        phone: (form.phone || "").trim() || null,
        email: (form.email || "").trim() || null,
      };
      delete payload.mailing_same;  // UI-only helper per ADR-0016 §1
      await api.post("/individuals", payload);
      toast.success("Individual added");
      setOpen(false);
      setForm({
        first_name: "", last_name: "", preferred_name: "",
        dob: "", intake_date: new Date().toISOString().slice(0, 10),
        primary_language: "English",
        county: "", mobility_notes: "",
        phone: "", email: "",
        home_address: { street: "", unit: "", city: "", state: "", zip: "", county: "" },
        mailing_same: true,
        mailing_address: { street: "", unit: "", city: "", state: "", zip: "", county: "" },
      });
      load(1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="individuals-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="accp-label">Directory</div>
          <h1 className="text-4xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            Individuals
          </h1>
          <p className="text-muted-foreground mt-1">
            The people your agency supports. Click a row for full details.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="accp-btn-secondary"
            onClick={() => setWizardOpen(true)}
            data-testid="ltss-wizard-btn"
          >
            <Wand2 size={16} /> Import from EVV
          </button>
          <button
            className="accp-btn-primary"
            onClick={() => setOpen(true)}
            data-testid="add-individual-btn"
          >
            <UserPlus size={16} /> Add individual
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(1)}
          placeholder="Search name, code, or MA number…"
          className="accp-input flex-1 min-w-[240px]"
          data-testid="individuals-search"
        />
        <button className="accp-btn-secondary" onClick={() => load(1)}>Search</button>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeDischarged}
            onChange={(e) => setIncludeDischarged(e.target.checked)}
            data-testid="individuals-include-discharged"
          />
          Show discharged
        </label>
        <div className="text-sm text-muted-foreground ml-auto">
          {total} {total === 1 ? "person" : "people"}
        </div>
      </div>

      {/* Table */}
      <div className="accp-card overflow-hidden">
        <div className="accp-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/30 text-left">
              <tr>
                <th className="p-4 accp-label">Code</th>
                <th className="p-4 accp-label">Name</th>
                <th className="p-4 accp-label">DOB</th>
                <th className="p-4 accp-label">Status</th>
                <th className="p-4 accp-label">Primary DSP</th>
                <th className="p-4 accp-label">County</th>
                <th className="p-4 accp-label"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    <UsersIcon size={32} className="mx-auto mb-2 opacity-40" />
                    No individuals yet. Add your first person to get started.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t hover:bg-accent/20 cursor-pointer"
                  onClick={() => setDetail(r)}
                  data-testid={`individual-row-${r.code}`}
                >
                  <td className="p-4 font-mono text-xs">{r.code}</td>
                  <td className="p-4 font-medium">
                    {r.last_name}, {r.first_name}
                    {r.preferred_name && (
                      <span className="text-muted-foreground"> ({r.preferred_name})</span>
                    )}
                  </td>
                  <td className="p-4">{r.dob || "—"}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs ${STATUS_STYLE[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">
                    {r.primary_dsp_id ? "assigned" : "—"}
                  </td>
                  <td className="p-4">{r.county || "—"}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {canManage && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setEditor({ individual: r, initialTab: "demographics" }); }}
                          className="p-1 rounded hover:bg-slate-100 text-primary"
                          title="Manage individual"
                          data-testid={`individual-edit-${r.code}`}>
                          <Pencil size={16} />
                        </button>
                      )}
                      <Eye size={16} className="text-muted-foreground" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination page={page} pages={pages} onPage={load} />

      {/* Create modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form
            onSubmit={submit}
            className="accp-card w-full max-w-2xl p-8 space-y-6 max-h-[90vh] overflow-y-auto"
            data-testid="individual-create-modal"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                New individual
              </h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="accp-label mb-1">First name *</div>
                <input required className="accp-input"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="ind-first-name" />
              </div>
              <div>
                <div className="accp-label mb-1">Last name *</div>
                <input required className="accp-input"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  data-testid="ind-last-name" />
              </div>
              <div>
                <div className="accp-label mb-1">Preferred name</div>
                <input className="accp-input"
                  value={form.preferred_name}
                  onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} />
              </div>
              <div>
                <div className="accp-label mb-1">Date of birth *</div>
                <input required type="date" className="accp-input"
                  value={form.dob}
                  onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  data-testid="ind-dob" />
              </div>
              <div>
                <div className="accp-label mb-1">Intake date *</div>
                <input required type="date" className="accp-input"
                  value={form.intake_date}
                  onChange={(e) => setForm({ ...form, intake_date: e.target.value })}
                  data-testid="ind-intake" />
              </div>
              <div>
                <div className="accp-label mb-1">Primary language</div>
                <input className="accp-input"
                  value={form.primary_language}
                  onChange={(e) => setForm({ ...form, primary_language: e.target.value })} />
              </div>
              <div>
                <div className="accp-label mb-1">County</div>
                <input className="accp-input"
                  value={form.county}
                  onChange={(e) => setForm({ ...form, county: e.target.value })} />
              </div>
              <div className="col-span-2">
                <div className="accp-label mb-1">Mobility / access notes</div>
                <textarea className="accp-input"
                  rows={2}
                  value={form.mobility_notes}
                  onChange={(e) => setForm({ ...form, mobility_notes: e.target.value })}
                  placeholder="e.g. Uses wheelchair; needs 2-person transfer" />
              </div>
            </div>

            {/* ADR-0016 — Participant contact & address (all optional). Ownership: Individual. */}
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="ind-address-block">
              <div className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Contact & Address (participant-owned, all optional)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="accp-label mb-1">Phone</div>
                  <input className="accp-input" type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="ind-phone" />
                </div>
                <div>
                  <div className="accp-label mb-1">Email</div>
                  <input className="accp-input" type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="ind-email" />
                </div>
              </div>
              <div className="text-xs font-medium text-slate-600">Home address</div>
              <div className="grid grid-cols-6 gap-2">
                <input className="accp-input col-span-4" placeholder="Street"
                  value={form.home_address.street}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, street: e.target.value } })}
                  data-testid="ind-home-street" />
                <input className="accp-input col-span-2" placeholder="Unit / Apt"
                  value={form.home_address.unit}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, unit: e.target.value } })} />
                <input className="accp-input col-span-3" placeholder="City"
                  value={form.home_address.city}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, city: e.target.value } })} />
                <input className="accp-input" placeholder="State"
                  value={form.home_address.state}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, state: e.target.value } })} />
                <input className="accp-input" placeholder="ZIP"
                  value={form.home_address.zip}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, zip: e.target.value } })} />
                <input className="accp-input" placeholder="County"
                  value={form.home_address.county}
                  onChange={(e) => setForm({ ...form, home_address: { ...form.home_address, county: e.target.value } })} />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={form.mailing_same}
                  onChange={(e) => setForm({ ...form, mailing_same: e.target.checked })}
                  data-testid="ind-mailing-same" />
                        {form.mailing_same
                  ? "Mailing address is the same as home address"
                  : "Use a different mailing address"}
              </label>
              {!form.mailing_same && (
                <div>
                  <div className="text-xs font-medium text-slate-600 mb-1">Mailing address</div>
                  <div className="grid grid-cols-6 gap-2">
                    <input className="accp-input col-span-4" placeholder="Street"
                      value={form.mailing_address.street}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, street: e.target.value } })} />
                    <input className="accp-input col-span-2" placeholder="Unit / Apt"
                      value={form.mailing_address.unit}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, unit: e.target.value } })} />
                    <input className="accp-input col-span-3" placeholder="City"
                      value={form.mailing_address.city}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, city: e.target.value } })} />
                    <input className="accp-input" placeholder="State"
                      value={form.mailing_address.state}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, state: e.target.value } })} />
                    <input className="accp-input" placeholder="ZIP"
                      value={form.mailing_address.zip}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, zip: e.target.value } })} />
                    <input className="accp-input" placeholder="County"
                      value={form.mailing_address.county}
                      onChange={(e) => setForm({ ...form, mailing_address: { ...form.mailing_address, county: e.target.value } })} />
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 italic">
                Per ADR-0016: this address is the participant&apos;s own address and is not automatically copied from any Location.
              </div>
            </div>

            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
              You can add emergency contacts, external identifiers, and service
              authorizations after creation. To move this individual to
              <span className="font-semibold"> active</span> status, at least one
              primary emergency contact, one primary external identifier, and one
              service authorization are required.
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" className="accp-btn-secondary"
                onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="accp-btn-primary" disabled={saving}
                data-testid="ind-create-submit">
                {saving ? "Saving…" : "Create individual"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Detail panel — Sprint 3 Individual Workspace */}
      {detail && (
        <IndividualWorkspace
          individual={detail}
          onClose={() => setDetail(null)}
          canManage={canManage}
          openEditor={(tab) => setEditor({ individual: detail, initialTab: tab || "demographics" })}
        />
      )}

      {/* B-1 Phase C successor — Admin/RN Individual Editor */}
      {editor && canManage && (
        <IndividualEditor
          individual={editor.individual}
          initialTab={editor.initialTab}
          onSaved={() => load(page)}
          onClose={() => setEditor(null)}
        />
      )}

      {/* Legacy skeleton (rollback fallback — disabled). */}
      {false && detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          data-testid="individual-detail-modal">
          <div className="accp-card w-full max-w-2xl p-8 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="accp-label">{detail.code}</div>
                <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
                  {detail.last_name}, {detail.first_name}
                </h2>
                <div className="mt-1">
                  <span className={`px-2 py-1 rounded text-xs ${STATUS_STYLE[detail.status] || ""}`}>
                    {detail.status}
                  </span>
                </div>
              </div>
              <button onClick={() => setDetail(null)}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Field label="DOB" v={detail.dob} />
              <Field label="Primary language" v={detail.primary_language} />
              <Field label="County" v={detail.county} />
              <Field label="Intake date" v={detail.intake_date} />
              <Field label="Guardian type" v={detail.guardian_type} />
              <Field label="Mobility notes" v={detail.mobility_notes} span2 />
              <Field label="Emergency contacts"
                v={`${(detail.emergency_contacts || []).length} contact(s)`} />
              <Field label="External identifiers"
                v={`${(detail.external_identifiers || []).filter((e) => !e.deprecated).length} active`} />
              <Field label="Service authorizations"
                v={`${(detail.service_authorizations || []).length} on file`} />
              <Field label="Authorized DSPs"
                v={`${(detail.authorized_dsp_ids || []).length}`} />
            </div>
            <div className="text-xs text-muted-foreground pt-4 border-t">
              Full detail editing (add contacts, identifiers, service authorizations,
              status transitions) ships in the next sprint.
            </div>
          </div>
        </div>
      )}

      {/* LTSS Import Wizard */}
      {wizardOpen && (
        <LtssWizard onClose={() => { setWizardOpen(false); load(1); }} />
      )}
    </div>
  );
}

function Field({ label, v, span2 = false }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <div className="accp-label mb-1">{label}</div>
      <div>{v || "—"}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual Workspace — Sprint 3 UI for the read-only /workspace endpoint.
// Aggregates recent timesheets, shifts, incidents, and segments into one
// participant-first 360° view. Per ADR-0012 this is a projection only —
// edits must be made in the owning module.
// ---------------------------------------------------------------------------
function IndividualWorkspace({ individual, onClose, canManage = false, openEditor }) {
  const [tab, setTab] = useState("summary");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/individuals/${individual.id}/workspace`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [individual.id]);

  const activeAuths = (data?.individual?.service_authorizations || [])
    .filter((sa) => !sa.effective_end || sa.effective_end >= new Date().toISOString().slice(0, 10));
  const reviewCount = data?.supervisor_queue?.segments_needing_authorization_review || 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="individual-workspace-modal">
      <div className="accp-card w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div>
            <div className="accp-label text-xs">{individual.code} · Participant workspace</div>
            <h2 className="text-3xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}
              data-testid="workspace-participant-name">
              {individual.last_name}, {individual.first_name}
            </h2>
            <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
              <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[individual.status] || ""}`}>
                {individual.status}
              </span>
              <span>DOB: {individual.dob || "—"}</span>
              {individual.county && <span>· {individual.county}</span>}
              <span>· {activeAuths.length} active authorization{activeAuths.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="workspace-close-btn" className="p-1 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Admin/RN quick action bar — Manage deep links open the owning
            edit surface (Individuals module) per ADR-0012. The Workspace
            itself remains read-only. */}
        {canManage && openEditor && (
          <div className="px-6 py-2 bg-slate-50 border-b border-border flex flex-wrap items-center gap-3 text-xs"
            data-testid="workspace-manage-bar">
            <span className="text-muted-foreground">Manage:</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("demographics")}
              data-testid="workspace-manage-demographics">Demographics</button>
            <span className="text-muted-foreground">·</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("contacts")}
              data-testid="workspace-manage-contacts">Emergency contacts</button>
            <span className="text-muted-foreground">·</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("identifiers")}
              data-testid="workspace-manage-identifiers">Identifiers</button>
            <span className="text-muted-foreground">·</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("authorizations")}
              data-testid="workspace-manage-authorizations">Service authorizations</button>
            <span className="text-muted-foreground">·</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("dsps")}
              data-testid="workspace-manage-dsps">DSP access</button>
            <span className="text-muted-foreground">·</span>
            <button className="text-primary hover:underline"
              onClick={() => openEditor("status")}
              data-testid="workspace-manage-status">Status</button>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 border-b border-border flex gap-1 -mb-px">
          {[
            ["summary", "Summary", ClipboardList],
            ["timesheets", `Timesheets (${(data?.recent_timesheets || []).length})`, Clock],
            ["shifts", `Shifts (${(data?.recent_shifts || []).length})`, UsersIcon],
            ["segments", `Segments (${(data?.recent_segments || []).length})`, ClipboardList],
            ["incidents", `Incidents (${(data?.recent_incidents || []).length})`, AlertTriangle],
          ].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`workspace-tab-${k}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"
              }`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && <div className="text-sm text-muted-foreground">Loading workspace…</div>}

          {!loading && tab === "summary" && data && (
            <div className="space-y-4">
              {/* B-1: Activation gaps — read-only surface from
                  validate_activation_prerequisites. Non-blocking; if
                  empty the banner is hidden. */}
              {(data.activation_gaps || []).length > 0 && (
                <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 flex items-start gap-3"
                  data-testid="workspace-activation-gaps">
                  <AlertTriangle size={18} className="text-rose-700 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-rose-900 mb-1">Activation prerequisites</div>
                    {data.activation_gaps.map((g, i) => (
                      <div key={i} className="text-rose-800">{g}</div>
                    ))}
                  </div>
                </div>
              )}

              {reviewCount > 0 && (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3"
                  data-testid="workspace-review-banner">
                  <AlertTriangle size={18} className="text-amber-700" />
                  <div className="flex-1 text-sm">
                    <span className="font-semibold text-amber-900">{reviewCount} segment{reviewCount === 1 ? "" : "s"}</span>
                    <span className="text-amber-800"> awaiting authorization review — visit the Supervisor Review Queue.</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-4 gap-4">
                <StatCard label="Active authorizations" value={activeAuths.length} />
                <StatCard label="External identifiers"
                  value={(individual.external_identifiers || []).filter((e) => !e.deprecated).length} />
                <StatCard label="Emergency contacts"
                  value={(individual.emergency_contacts || []).length} />
                {/* B-1: undocumented visits — read from workspace projection */}
                <StatCard
                  label="Undocumented visits"
                  value={data.undocumented_visits?.count ?? 0}
                  testid="workspace-undocumented-stat"
                />
              </div>

              {/* B-1: per-authorization utilization (this week) */}
              {(data.per_authorization_utilization?.authorizations || []).length > 0 && (
                <div data-testid="workspace-utilization">
                  <div className="accp-label mb-2">Authorization utilization (this week)</div>
                  <div className="text-xs text-muted-foreground mb-2">
                    {data.per_authorization_utilization.week_start?.slice(0, 10)} → {data.per_authorization_utilization.week_end?.slice(0, 10)}
                  </div>
                  {data.per_authorization_utilization.authorizations.map((u) => {
                    const cap = u.authorized_units_weekly || 0;
                    const pct = cap > 0 ? Math.min(100, Math.round((u.hours_used_this_week / cap) * 100)) : 0;
                    return (
                      <div key={u.authorization_id}
                        className="p-3 rounded border border-border mb-2 text-sm"
                        data-testid={`workspace-utilization-row-${u.authorization_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-semibold">{u.waiver_code} · {u.funding_source_code}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {u.hours_used_this_week}h / {cap || "—"}h
                          </div>
                        </div>
                        {cap > 0 && (
                          <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div>
                <div className="accp-label mb-2">Active service authorizations</div>
                {activeAuths.length === 0 && <div className="text-sm text-muted-foreground">None on file.</div>}
                {activeAuths.map((sa) => (
                  <div key={sa.id} className="p-3 rounded border border-border mb-2 text-sm">
                    <div className="font-mono text-xs text-muted-foreground">{sa.id}</div>
                    <div className="font-semibold">{sa.waiver_code} · {sa.funding_source_code}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Services: {(sa.service_types || []).join(", ") || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {sa.effective_start} → {sa.effective_end || "open"}
                    </div>
                  </div>
                ))}
              </div>

              {/* B-1: open bid shifts targeting this Individual */}
              {(data.open_bid_shifts_for_individual || []).length > 0 && (
                <div data-testid="workspace-open-bids">
                  <div className="accp-label mb-2">Open shifts targeting this participant</div>
                  {data.open_bid_shifts_for_individual.map((s) => (
                    <div key={s.id}
                      className="p-3 rounded border border-border mb-2 text-sm flex items-center justify-between"
                      data-testid={`workspace-open-bid-row-${s.id}`}>
                      <div>
                        <div className="font-mono text-xs text-muted-foreground">{s.code}</div>
                        <div className="mt-1">
                          {new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {s.pending_bids_count} pending bid{s.pending_bids_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* B-1: recent audit — admin/rn ONLY. The key is absent for
                  DSPs (server enforces the omission), so this block
                  never renders for a DSP caller. */}
              {Array.isArray(data.recent_audit) && data.recent_audit.length > 0 && (
                <div data-testid="workspace-recent-audit">
                  <div className="accp-label mb-2">Recent activity (audit trail)</div>
                  <div className="rounded border border-border overflow-hidden text-xs">
                    {data.recent_audit.map((a) => (
                      <div key={a.code}
                        className="px-3 py-2 border-b border-border last:border-b-0 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-slate-500">{new Date(a.timestamp).toLocaleString()}</span>
                          <span className="uppercase tracking-wide text-primary">{a.action}</span>
                          <span className="text-muted-foreground truncate">{a.user_name || a.user_code} · {a.user_role}</span>
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${a.severity === "high" ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>
                          {a.severity}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Preview only. Open the Audit Log for `before` / `after` / `diff` details.
                  </div>
                </div>
              )}

              <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                {data.note}
              </div>
            </div>
          )}

          {!loading && tab === "timesheets" && (
            <TimesheetList rows={data?.recent_timesheets || []} />
          )}

          {!loading && tab === "shifts" && (
            <ShiftList rows={data?.recent_shifts || []} />
          )}

          {!loading && tab === "segments" && (
            <SegmentList rows={data?.recent_segments || []} />
          )}

          {!loading && tab === "incidents" && (
            <IncidentList rows={data?.recent_incidents || []} />
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="p-4 rounded-lg border border-border bg-slate-50"
      data-testid={testid}>
      <div className="accp-label text-xs">{label}</div>
      <div className="text-3xl font-semibold text-primary mt-1">{value}</div>
    </div>
  );
}

function TimesheetList({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No recent timesheets.</div>;
  return (
    <div className="space-y-2" data-testid="workspace-timesheet-list">
      {rows.map((t) => (
        <div key={t.id} className="p-3 rounded border border-border text-sm flex items-center justify-between">
          <div>
            <div className="font-mono text-xs text-muted-foreground">{t.code}</div>
            <div className="mt-1">
              {new Date(t.clock_in).toLocaleString()}
              {t.clock_out && <> → {new Date(t.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-semibold">{t.hours ? `${t.hours}h` : "—"}</div>
            <div className="text-xs text-muted-foreground">{t.approval_status}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ShiftList({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No shifts in ±30d window.</div>;
  return (
    <div className="space-y-2" data-testid="workspace-shift-list">
      {rows.map((s) => (
        <div key={s.id} className="p-3 rounded border border-border text-sm">
          <div className="font-mono text-xs text-muted-foreground">{s.code}</div>
          <div className="mt-1 flex items-center gap-2">
            <Clock size={12} />
            {new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {s.status} · {s.service_context || "participant"}
          </div>
        </div>
      ))}
    </div>
  );
}

function SegmentList({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No documented service segments yet.</div>;
  return (
    <div className="space-y-2" data-testid="workspace-segment-list">
      {rows.map((s) => (
        <div key={s.id} className="p-3 rounded border border-border text-sm">
          <div className="flex items-center justify-between">
            <div className="font-semibold">{s.service_type}</div>
            <SegmentStatusBadge status={s.documentation_status} />
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {new Date(s.start_time).toLocaleString()} → {new Date(s.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
          {s.service_authorization_id && (
            <div className="mt-1 text-xs font-mono text-muted-foreground">Auth: {s.service_authorization_id}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function SegmentStatusBadge({ status }) {
  const styles = {
    complete: "bg-green-100 text-green-800",
    pending: "bg-slate-100 text-slate-700",
    needs_authorization_review: "bg-amber-100 text-amber-900",
  };
  const labels = {
    complete: "Complete",
    pending: "Pending",
    needs_authorization_review: "Needs review",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${styles[status] || ""}`}>
      {labels[status] || status}
    </span>
  );
}

function IncidentList({ rows }) {
  if (!rows.length) return <div className="text-sm text-muted-foreground">No incidents.</div>;
  return (
    <div className="space-y-2" data-testid="workspace-incident-list">
      {rows.map((i) => (
        <div key={i.id} className="p-3 rounded border border-border text-sm">
          <div className="flex items-center justify-between">
            <div className="font-mono text-xs text-muted-foreground">{i.code}</div>
            <span className={`px-2 py-0.5 rounded-full text-xs ${i.severity === "critical" || i.severity === "high" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>
              {i.severity}
            </span>
          </div>
          <div className="mt-1 font-semibold">{i.what}</div>
          <div className="mt-1 text-xs text-muted-foreground">{new Date(i.when).toLocaleString()} · {i.where}</div>
        </div>
      ))}
    </div>
  );
}



function LtssWizard({ onClose }) {
  const [phase, setPhase] = useState("loading");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/individuals/ltss-sync/preview").then((r) => {
      setPreview(r.data);
      const sel = {};
      for (const c of r.data.new_candidates) {
        sel[c.client_identifier] = {
          checked: true,
          first_name: c.proposed_first_name,
          last_name: c.proposed_last_name || "UNKNOWN",
          dob: "",
        };
      }
      setSelected(sel);
      setPhase("review");
    }).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [onClose]);

  const toggle = (id) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id], checked: !s[id]?.checked } }));

  const editField = (id, field, val) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id], [field]: val } }));

  const submit = async () => {
    const selections = Object.entries(selected)
      .filter(([, v]) => v.checked)
      .map(([client_identifier, v]) => ({
        client_identifier,
        first_name: v.first_name,
        last_name: v.last_name,
        dob: v.dob || null,
      }));
    if (selections.length === 0) {
      toast.error("Select at least one candidate to import.");
      return;
    }
    setPhase("applying");
    try {
      const r = await api.post("/individuals/ltss-sync/apply", { selections });
      setResult(r.data);
      setPhase("done");
      toast.success(`Created ${r.data.summary.created_count} individuals`);
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("review");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      data-testid="ltss-wizard-modal">
      <div className="accp-card w-full max-w-4xl p-8 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <div className="accp-label">Wizard</div>
            <h2 className="text-2xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
              Import Individuals from EVV
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Scans your imported EVV timesheets and creates matching Individual
              records. Existing matches are skipped — safe to re-run anytime.
            </p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {phase === "loading" && (
          <div className="p-8 text-center text-muted-foreground">Scanning EVV imports…</div>
        )}

        {phase === "review" && preview && (
          <>
            <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900 grid grid-cols-3 gap-2">
              <div><span className="font-semibold">{preview.summary.total_unique_clients}</span> unique ClientIdentifiers found</div>
              <div><span className="font-semibold text-green-700">{preview.summary.already_mapped_count}</span> already mapped</div>
              <div><span className="font-semibold text-purple-700">{preview.summary.new_candidate_count}</span> new to seed</div>
            </div>

            {preview.new_candidates.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                No new candidates — every ClientIdentifier is already mapped.
              </div>
            ) : (
              <div className="accp-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-accent/30 text-left">
                    <tr>
                      <th className="p-3 w-8"></th>
                      <th className="p-3 accp-label">Client ID</th>
                      <th className="p-3 accp-label">First name</th>
                      <th className="p-3 accp-label">Last name</th>
                      <th className="p-3 accp-label">DOB</th>
                      <th className="p-3 accp-label">Visits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.new_candidates.map((c) => {
                      const s = selected[c.client_identifier] || {};
                      return (
                        <tr key={c.client_identifier} className="border-t">
                          <td className="p-3">
                            <input type="checkbox" checked={!!s.checked}
                              onChange={() => toggle(c.client_identifier)}
                              data-testid={`ltss-check-${c.client_identifier}`} />
                          </td>
                          <td className="p-3 font-mono text-xs">{c.client_identifier}</td>
                          <td className="p-3">
                            <input className="accp-input" value={s.first_name || ""}
                              onChange={(e) => editField(c.client_identifier, "first_name", e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input className="accp-input" value={s.last_name || ""}
                              onChange={(e) => editField(c.client_identifier, "last_name", e.target.value)} />
                          </td>
                          <td className="p-3">
                            <input type="date" className="accp-input" value={s.dob || ""}
                              onChange={(e) => editField(c.client_identifier, "dob", e.target.value)} />
                          </td>
                          <td className="p-3 text-xs">
                            {c.visit_count}
                            <div className="text-muted-foreground">
                              {(c.first_seen || "").slice(0, 10)} → {(c.last_seen || "").slice(0, 10)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {preview.already_mapped.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground">
                  Show {preview.already_mapped.length} already-mapped clients
                </summary>
                <ul className="mt-2 space-y-1 pl-4">
                  {preview.already_mapped.map((m) => (
                    <li key={m.client_identifier} className="text-xs">
                      <span className="font-mono">{m.client_identifier}</span> →{" "}
                      <span className="font-medium">{m.existing_individual_code}</span>{" "}
                      {m.existing_individual_name}
                    </li>
                  ))}
                </ul>
              </details>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button className="accp-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="accp-btn-primary" onClick={submit}
                data-testid="ltss-wizard-apply"
                disabled={preview.new_candidates.length === 0}>
                Create selected individuals
              </button>
            </div>
          </>
        )}

        {phase === "applying" && (
          <div className="p-8 text-center text-muted-foreground">Creating individuals…</div>
        )}

        {phase === "done" && result && (
          <div className="space-y-3">
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-900">
              Created <span className="font-semibold">{result.summary.created_count}</span> individuals.
              {result.summary.skipped_count > 0 && (
                <> Skipped {result.summary.skipped_count} already-mapped.</>
              )}
              {result.summary.error_count > 0 && (
                <> <span className="text-red-700">Errors: {result.summary.error_count}</span></>
              )}
            </div>
            {result.created.length > 0 && (
              <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
                {result.created.map((c) => (
                  <li key={c.individual_id} data-testid={`ltss-created-${c.individual_code}`}>
                    <span className="font-mono font-semibold">{c.individual_code}</span> ←{" "}
                    <span className="font-mono text-muted-foreground">{c.client_identifier}</span>
                  </li>
                ))}
              </ul>
            )}
            {result.errors.length > 0 && (
              <ul className="text-xs space-y-1 text-red-700">
                {result.errors.map((e, i) => (
                  <li key={i}>{e.client_identifier}: {e.error}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button className="accp-btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
